import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useJobsWs } from "@/lib/ws";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Activity, Ban, RotateCcw } from "lucide-react";

import { JobsApi, type JobRow } from "@/lib/services";
import { fa } from "@/locales/fa";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { formatBytes, formatDuration, jobDurationMs, num, timeAgo } from "@/lib/utils";

const PAGE_SIZE = 50;

const STATES = ["", "pending", "running", "done", "failed", "cancelled"] as const;
// `account_pkgacct` is the graveyard's own kind, and it is LAST on purpose: it
// is not part of the routine backup product, the list excludes it by default,
// and picking it here is the explicit opt-in for an operator who wants to see
// captures alongside everything else. Its home is the «قبرستان» screen.
const KINDS = ["", "account_full", "account_db", "account_meta", "verify", "prune", "account_pkgacct"] as const;

export function JobsPage() {
  const [state, setState] = useState<string>("");
  const [kind, setKind] = useState<string>("");
  const [page, setPage] = useState(0);

  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["jobs", { state, kind, page }],
    queryFn: () => JobsApi.list({ state: state || undefined, kind: kind || undefined, limit: PAGE_SIZE, offset: page * PAGE_SIZE }),
    refetchInterval: 30_000, // WS handles freshness; polling is just a safety net
  });

  // Subscribe to live job deltas; invalidate query on any change so the
  // table reflects them immediately. WsState is exposed in the badge below.
  const ws = useJobsWs();
  useEffect(() => {
    if (!ws.lastMessage) return;
    void qc.invalidateQueries({ queryKey: ["jobs"] });
  }, [ws.lastMessage, qc]);

  // `nowTick` is bumped every second so running jobs' elapsed-time
  // counter updates in place without waiting for a WS delta. We only
  // set up the timer when at least one job is `running` to avoid an
  // always-on render churn (the page is rendered constantly by
  // dashboards left open in tabs).
  const hasRunning = (data?.items ?? []).some((j) => j.state === "running");
  const [nowTick, setNowTick] = useState(() => Date.now());
  useEffect(() => {
    if (!hasRunning) return;
    const id = window.setInterval(() => setNowTick(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [hasRunning]);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold flex items-center gap-2">
            <Activity className="size-5 text-primary" />
            {fa.jobs.title}
          </h1>
          <p className="text-sm text-muted-foreground">جاب‌های موتور خودکاری‌شده و در حال اجرا</p>
        </div>

        <div className="flex items-center gap-2">
          <Select value={state || "all"} onValueChange={(v) => { setState(v === "all" ? "" : v); setPage(0); }}>
            <SelectTrigger className="w-32"><SelectValue placeholder={fa.common.state} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{fa.common.all}</SelectItem>
              {STATES.filter(Boolean).map((s) => (
                <SelectItem key={s} value={s}>{fa.jobs.states[s as keyof typeof fa.jobs.states]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={kind || "all"} onValueChange={(v) => { setKind(v === "all" ? "" : v); setPage(0); }}>
            <SelectTrigger className="w-40"><SelectValue placeholder={fa.jobs.kind} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{fa.common.all}</SelectItem>
              {KINDS.filter(Boolean).map((k) => (
                <SelectItem key={k} value={k}>{fa.jobs.kinds[k as keyof typeof fa.jobs.kinds]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-muted-foreground">
            مجموع: <span className="text-foreground">{num(data?.total ?? 0)}</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 10 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
            </div>
          ) : (data?.items ?? []).length === 0 ? (
            <div className="py-10 text-center text-muted-foreground">{fa.common.empty}</div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{fa.jobs.kind}</TableHead>
                    <TableHead>{fa.jobs.account}</TableHead>
                    <TableHead>{fa.jobs.server}</TableHead>
                    <TableHead>{fa.common.state}</TableHead>
                    <TableHead>{fa.jobs.progress}</TableHead>
                    <TableHead>{fa.common.size}</TableHead>
                    <TableHead>{fa.jobs.started}</TableHead>
                    <TableHead>{fa.jobs.duration}</TableHead>
                    <TableHead className="w-10" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data!.items.map((j) => <JobRowItem key={j.id} job={j} nowMs={nowTick} />)}
                </TableBody>
              </Table>

              {(data?.total ?? 0) > PAGE_SIZE && (
                <div className="flex items-center justify-between mt-3">
                  <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>قبلی</Button>
                  <div className="text-xs text-muted-foreground">صفحه {num(page + 1)} از {num(Math.ceil((data?.total ?? 0) / PAGE_SIZE))}</div>
                  <Button variant="outline" size="sm" disabled={(page + 1) * PAGE_SIZE >= (data?.total ?? 0)} onClick={() => setPage((p) => p + 1)}>بعدی</Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function JobRowItem({ job, nowMs }: { job: JobRow; nowMs: number }) {
  const qc = useQueryClient();
  const cancel = useMutation({
    mutationFn: () => JobsApi.cancel(job.id),
    onSuccess: () => { toast.success("درخواست لغو ارسال شد"); qc.invalidateQueries({ queryKey: ["jobs"] }); },
    onError: (e: Error) => toast.error("ناموفق", { description: e.message }),
  });
  const retry = useMutation({
    mutationFn: () => JobsApi.retry(job.id),
    onSuccess: () => { toast.success("جاب مجدداً صف شد"); qc.invalidateQueries({ queryKey: ["jobs"] }); },
    onError: (e: Error) => toast.error("ناموفق", { description: e.message }),
  });
  const retryable =
    (job.state === "failed" || job.state === "cancelled") &&
    (job.kind === "account_full" || job.kind === "account_db" || job.kind === "account_meta");

  const variant =
    job.state === "done" ? "success"
    : job.state === "failed" ? "destructive"
    : job.state === "running" ? "default"
    : job.state === "cancelled" ? "outline"
    : "secondary";

  return (
    <TableRow>
      <TableCell>{fa.jobs.kinds[job.kind]}</TableCell>
      <TableCell className="lat-num">{job.accountUser ?? "—"}</TableCell>
      <TableCell>{job.serverName ?? "—"}</TableCell>
      <TableCell><Badge variant={variant as never}>{fa.jobs.states[job.state]}</Badge></TableCell>
      <TableCell className="min-w-40">
        {job.state === "running" ? (
          <div className="flex items-center gap-2">
            <Progress value={job.progress} className="w-24" />
            <span className="text-[11px] text-muted-foreground tabular">{num(job.progress)}٪</span>
          </div>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </TableCell>
      <TableCell className="tabular">{job.bytesWritten != null ? formatBytes(job.bytesWritten) : "—"}</TableCell>
      <TableCell className="text-muted-foreground">{job.startedAt ? timeAgo(job.startedAt) : timeAgo(job.createdAt)}</TableCell>
      <TableCell className="text-muted-foreground whitespace-nowrap">
        {(() => {
          const d = jobDurationMs(job.startedAt, job.finishedAt, nowMs);
          if (d == null) return "—";
          if (job.state === "running") return formatDuration(d);
          if (job.finishedAt) return formatDuration(d);
          return "—";
        })()}
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-1 justify-end">
          <Button asChild variant="ghost" size="sm">
            <Link to={`/jobs/${job.id}`}>{fa.jobs.logs}</Link>
          </Button>
          {(job.state === "running" || job.state === "pending") && (
            <Button variant="ghost" size="icon" onClick={() => cancel.mutate()} title={fa.jobs.cancel}>
              <Ban className="size-4 text-destructive" />
            </Button>
          )}
          {retryable && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => retry.mutate()}
              disabled={retry.isPending}
              title="اجرای مجدد"
            >
              <RotateCcw className="size-4" />
            </Button>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}
